import { addDays, DateOnly } from '@if/core/date'
import type { PendingLenderPaymentDto } from '@src/api/lender-payment/pending-lender-payments-api'
import { EDisbursementTarget } from '@src/types/constants'

// Pure grouping and selection helpers for the "Débourser" selection screen: pending lender
// payments grouped by payee type (one card each), then by payee (one header each).

/**
 * How far ahead the screen ever reaches. Even with "show future payments" ticked, nothing beyond
 * this window is listed: a payment further out is not actionable and only crowds the list.
 */
export const FUTURE_PAYMENT_WINDOW_DAYS = 3

/** The last day the screen shows, whatever the user ticks. */
export const lastVisibleDay = (today: DateOnly): DateOnly =>
  new DateOnly(addDays(today.toDate(), FUTURE_PAYMENT_WINDOW_DAYS))

/**
 * Past the window the screen never lists a payment, tick or no tick. Unlike isFuturePayment this
 * reads the date alone: the cap is about how far the page looks ahead, not about what can be paid.
 */
export const isBeyondFutureWindow = (payment: PendingLenderPaymentDto, today: DateOnly): boolean =>
  new DateOnly(payment.paymentOn) > lastVisibleDay(today)

/**
 * A payment due after today cannot be disbursed yet, so it is never selectable and is hidden
 * unless the user asks to see what is coming.
 */
export const isFuturePayment = (payment: PendingLenderPaymentDto, today: DateOnly): boolean =>
  !payment.isAccountToAccountPayout && new DateOnly(payment.paymentOn) > today

/** Why a payment's checkbox is blocked, or null when it can be selected. */
export type BlockReason = 'closingNotFinalized' | 'future'

/**
 * Two things block a payment: its closing is not finalized yet, or it is simply not due. Both block
 * the same way.
 */
export const blockReasonOf = (payment: PendingLenderPaymentDto, today: DateOnly): BlockReason | null => {
  if (payment.isPendingClosingFinalization) return 'closingNotFinalized'
  if (isFuturePayment(payment, today)) return 'future'
  return null
}

export interface PayeeGroup {
  payeeId: string
  label: string
  payments: PendingLenderPaymentDto[]
  /** The subset a checkbox can tick: everything `blockReasonOf` clears. */
  selectable: PendingLenderPaymentDto[]
  total: number
}

export interface PayeeTypeGroup {
  payeeType: EDisbursementTarget
  payees: PayeeGroup[]
  payments: PendingLenderPaymentDto[]
  selectable: PendingLenderPaymentDto[]
  total: number
}

/**
 * The amount as the screen shows and sums it. The API sends every payment positive, as stored; an
 * account-to-account payout is money coming back in rather than going out — the Monolith's own
 * IDeboursable.Amount negates it the same way — so it is negated here: formatCurrency prints it in
 * parentheses, and the totals subtract it.
 */
export const signedAmountOf = (payment: PendingLenderPaymentDto): number =>
  payment.isAccountToAccountPayout ? -payment.amount : payment.amount

export const sumAmounts = (payments: PendingLenderPaymentDto[]) =>
  payments.reduce((sum, p) => sum + signedAmountOf(p), 0)

// A borrower payee is the loan's own borrower; a merchant is named by the Merchants API, and falls
// back to the payee id, which is all an unresolved merchant has.
const payeeLabel = (payment: PendingLenderPaymentDto) =>
  payment.payeeType === EDisbursementTarget.Borrower ? payment.borrowerFullName : (payment.payeeName ?? payment.payeeId)

export const groupByPayeeType = (payments: PendingLenderPaymentDto[], today: DateOnly): PayeeTypeGroup[] => {
  const byType = new Map<EDisbursementTarget, Map<string, PendingLenderPaymentDto[]>>()
  for (const payment of payments) {
    const byPayee = byType.get(payment.payeeType) ?? new Map<string, PendingLenderPaymentDto[]>()
    byType.set(payment.payeeType, byPayee)
    byPayee.set(payment.payeeId, [...(byPayee.get(payment.payeeId) ?? []), payment])
  }

  // Merchants first, then borrowers — the order the buttons above act on them.
  const typeOrder = [EDisbursementTarget.Merchant, EDisbursementTarget.Borrower]
  return typeOrder
    .filter((type) => byType.has(type))
    .map((payeeType) => {
      const byPayee = byType.get(payeeType)!
      const payees: PayeeGroup[] = [...byPayee.entries()]
        .map(([payeeId, group]) => ({
          payeeId,
          label: payeeLabel(group[0]),
          payments: group,
          selectable: group.filter((p) => blockReasonOf(p, today) === null),
          total: sumAmounts(group),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      const typePayments = payees.flatMap((p) => p.payments)
      return {
        payeeType,
        payees,
        payments: typePayments,
        selectable: payees.flatMap((p) => p.selectable),
        total: sumAmounts(typePayments),
      }
    })
}

// A disbursement sends money out, so a payee whose selected payments add up to a negative amount
// cannot be submitted: its account-to-account credits outweigh what it is owed. Only payees with a
// selection are judged — an untouched payee is not a problem to report.
export const selectedTotalOf = (payee: PayeeGroup, selected: ReadonlySet<number>): number =>
  sumAmounts(payee.payments.filter((p) => selected.has(p.operationId)))

export const hasNegativeSelectedTotal = (payee: PayeeGroup, selected: ReadonlySet<number>): boolean =>
  payee.payments.some((p) => selected.has(p.operationId)) && selectedTotalOf(payee, selected) < 0

/**
 * The amount above which one payee's disbursements for a single day warrant a warning.
 *
 * The story calls this a system parameter, but the LMS has no system-parameter mechanism yet —
 * no Config table, no endpoint, no hook. It therefore lives here until one exists, so the rule
 * itself can ship; swapping the constant for a fetched value is the only change needed then.
 */
export const DAILY_DISBURSEMENT_WARNING_THRESHOLD = 500_000

/**
 * The largest amount a payee would be sent on any single day. Account-to-account payouts are left
 * out and only positive amounts count: the warning is about how much money leaves for that merchant
 * in a day, not about the net after credits. Selection plays no part — the exposure exists whether
 * or not the user ticked anything.
 */
export const highestDailyTotalOf = (payee: PayeeGroup): number => {
  const perDay = new Map<string, number>()
  for (const payment of payee.payments) {
    if (payment.isAccountToAccountPayout || payment.amount <= 0) continue
    perDay.set(payment.paymentOn, (perDay.get(payment.paymentOn) ?? 0) + payment.amount)
  }
  return perDay.size === 0 ? 0 : Math.max(...perDay.values())
}

export const exceedsDailyThreshold = (payee: PayeeGroup): boolean =>
  highestDailyTotalOf(payee) > DAILY_DISBURSEMENT_WARNING_THRESHOLD

export type SelectionState = 'none' | 'some' | 'all'

/** Reads the state of a group's "select all" box. Pass the group's `selectable`, never its
 * `payments`: a group holding a blocked payment could otherwise never reach `all`. */
export const selectionStateOf = (
  payments: PendingLenderPaymentDto[],
  selected: ReadonlySet<number>,
): SelectionState => {
  const count = payments.filter((p) => selected.has(p.operationId)).length
  if (count === 0) return 'none'
  return count === payments.length ? 'all' : 'some'
}

// Toggling a group: a partially or fully selected group clears, an empty one fills.
export const toggleAll = (
  payments: PendingLenderPaymentDto[],
  selected: ReadonlySet<number>,
  select: boolean,
): Set<number> => {
  const next = new Set(selected)
  for (const payment of payments) {
    if (select) next.add(payment.operationId)
    else next.delete(payment.operationId)
  }
  return next
}

/** How many distinct payees the payments go to. A cheque pays exactly one. */
export const payeeCountOf = (payments: PendingLenderPaymentDto[]): number =>
  new Set(payments.map((p) => p.payeeId)).size

/**
 * The selection minus what the list no longer holds. A disbursed payment leaves the pending list,
 * and the form would otherwise keep its id selected, invisible, ready to be sent again.
 */
export const keepListed = (selected: ReadonlySet<number>, payments: PendingLenderPaymentDto[]): Set<number> => {
  const listed = new Set(payments.map((p) => p.operationId))
  return new Set([...selected].filter((id) => listed.has(id)))
}

export const toggleOne = (operationId: number, selected: ReadonlySet<number>): Set<number> => {
  const next = new Set(selected)
  if (next.has(operationId)) next.delete(operationId)
  else next.add(operationId)
  return next
}
